import { useCallback, useEffect, useRef, useState } from "react";

import { useResumeIdVerification } from "@/api/generated";
import { captureException } from "@/sentry";
import { useResolvedTheme } from "@/theme/helpers";

// Persona's web SDK can call onCancel with optional fields.
// Match that optionality to avoid TS variance issues.
type PersonaCancelPayload = {
  inquiryId?: string;
  sessionToken?: string;
};

type PersonaCompletePayload = {
  inquiryId: string;
  status: string;
};

type UsePersonaEmbeddedInquiryArgs = {
  // Render inside the caller's iframe (see `inlineIframeRef`) instead of Persona's body overlay.
  shouldRenderInline?: boolean;
  onComplete?: (payload: PersonaCompletePayload) => void;
  onCancel?: (payload: PersonaCancelPayload) => void;
  onPersonaError?: (err: unknown) => void;
  onResumeError?: (err: unknown) => void;
};

type PersonaClient = {
  open: () => void;
  cancel?: (force?: boolean) => void;
};

type InlineSession = {
  inquiryId: string;
  sessionToken: string;
};

const OVERLAY_STYLE_ID = "persona-overlay-above-dialogs";

// Persona's overlay ships at z-index 9999 under <body>, but Tamagui dialogs stack at 100000 and set
// pointer-events: none on <body>, so on desktop the ID check opened behind the modal, unclickable.
function raisePersonaOverlayAboveDialogs() {
  if (document.getElementById(OVERLAY_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = OVERLAY_STYLE_ID;
  style.textContent = `
    body .persona-widget__overlay {
      z-index: 200000 !important;
      pointer-events: auto;
    }
  `;

  document.head.appendChild(style);
}

async function loadPersona() {
  const mod = await import("persona");
  return mod.default || mod;
}

export function usePersonaEmbeddedInquiry(
  args: UsePersonaEmbeddedInquiryArgs = {},
) {
  const {
    shouldRenderInline = false,
    onComplete,
    onCancel,
    onPersonaError,
    onResumeError,
  } = args;

  const resolvedTheme = useResolvedTheme();

  const clientRef = useRef<PersonaClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inlineSession, setInlineSession] = useState<InlineSession | null>(
    null,
  );
  const [inlineIframe, setInlineIframe] = useState<HTMLIFrameElement | null>(
    null,
  );
  const [isInlineReady, setIsInlineReady] = useState(false);

  // The inline iframe outlives renders, so its Persona listeners read the latest callbacks.
  const callbacksRef = useRef({ onComplete, onCancel, onPersonaError });
  callbacksRef.current = { onComplete, onCancel, onPersonaError };

  const resumeInquiryMutation = useResumeIdVerification({
    mutation: {
      onSuccess: async ({
        session_token: sessionToken,
        inquiry_id: inquiryId,
      }) => {
        setError(null);

        if (shouldRenderInline) {
          setIsInlineReady(false);
          setInlineSession({ inquiryId, sessionToken });
          return;
        }

        // If a client is already around (e.g., user clicked twice), cancel it.
        try {
          clientRef.current?.cancel?.(true);
        } catch {
          // ignore
        } finally {
          clientRef.current = null;
        }

        try {
          const Persona = await loadPersona();
          raisePersonaOverlayAboveDialogs();

          const client = new Persona.Client({
            inquiryId,
            sessionToken,
            styleVariant: resolvedTheme,

            onReady: () => client.open(),

            onComplete: (payload: PersonaCompletePayload) => {
              onComplete?.(payload);
            },

            onCancel: (payload: PersonaCancelPayload) => {
              onCancel?.(payload);
            },

            onError: (err: unknown) => {
              captureException("Persona embed flow error", {
                extra: {
                  category: "persona",
                  error: err,
                  inquiryId,
                },
              });
              setError("Persona error");
              onPersonaError?.(err);
            },
          }) as unknown as PersonaClient;

          clientRef.current = client;
        } catch (importError) {
          captureException("Failed to load Persona SDK", {
            extra: {
              category: "persona",
              error: importError,
              inquiryId,
            },
          });
          setError("Failed to load Persona");
          onPersonaError?.(importError);
        }
      },

      onError: (err: unknown) => {
        captureException("Failed to start ID verification", {
          extra: {
            category: "persona",
            error: err,
          },
        });
        setError("Failed to start verification");
        onResumeError?.(err);
      },
    },
  });

  const start = useCallback(() => {
    if (resumeInquiryMutation.isPending) return;
    setError(null);
    resumeInquiryMutation.mutate();
  }, [resumeInquiryMutation.isPending, resumeInquiryMutation.mutate]);

  const resetError = useCallback(() => setError(null), []);

  const closeInline = useCallback(() => {
    setInlineSession(null);
    setIsInlineReady(false);
  }, []);

  useEffect(() => {
    if (!inlineSession || !inlineIframe) return;

    let isCancelled = false;
    let teardownEvents: (() => void) | null = null;
    const { inquiryId, sessionToken } = inlineSession;

    const reportPersonaError = (err: unknown) => {
      captureException("Persona inline flow error", {
        extra: { category: "persona", error: err, inquiryId },
      });
      setError("Persona error");
      setInlineSession(null);
      callbacksRef.current.onPersonaError?.(err);
    };

    loadPersona()
      .then((Persona) => {
        if (isCancelled) return;

        const containerId = `persona-inline-${inquiryId}`;
        teardownEvents = Persona.setupEvents(containerId, {
          onLoad: null,
          onReady: () => setIsInlineReady(true),
          onComplete: (payload: PersonaCompletePayload) => {
            setInlineSession(null);
            callbacksRef.current.onComplete?.(payload);
          },
          onEvent: null,
          onCancel: (payload: PersonaCancelPayload) => {
            setInlineSession(null);
            callbacksRef.current.onCancel?.(payload);
          },
          onError: reportPersonaError,
          templateId: null,
          templateVersionId: null,
          host: null,
        });
        Persona.setupIframe(inlineIframe, containerId, "inline", {
          inquiryId,
          sessionToken,
          styleVariant: resolvedTheme,
        });
      })
      .catch(reportPersonaError);

    return () => {
      isCancelled = true;
      teardownEvents?.();
    };
  }, [inlineSession, inlineIframe, resolvedTheme]);

  // Auto-cleanup
  useEffect(() => {
    return () => {
      try {
        clientRef.current?.cancel?.(true);
      } catch {
        // ignore
      } finally {
        clientRef.current = null;
      }
    };
  }, []);

  return {
    start,
    isStarting: resumeInquiryMutation.isPending,
    error,
    resetError,
    isInlineOpen: inlineSession !== null,
    isInlineReady,
    inlineIframeRef: setInlineIframe,
    closeInline,
  };
}
